import React from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
    GraduationCap, 
    Check, 
    Video,
    Clock,
    Plane,
    Star,
    Users,
    FileCheck,
    MessageCircle,
    Sparkles,
    Calendar,
    Target,
    Award,
    Heart,
    ExternalLink
} from 'lucide-react';

// Square payment link for mentorship
const SQUARE_MENTORSHIP_URL = "https://square.link/u/zLDsZv7W";

export default function Mentorship() {
    const { user } = useAuth();

    const handlePurchase = () => {
        // Redirect to Square checkout
        window.open(SQUARE_MENTORSHIP_URL, '_blank');
    };

    const phases = [
        {
            number: "1",
            title: "Initial Assessment & Personalized Roadmap",
            sessions: "1-2 sessions, 60 minutes each",
            items: [
                "Deep-dive call to review your resume, background, and target airlines",
                "Identify strengths, gaps, and potential red flags",
                "Create a customized 4-8 week plan tailored to your needs",
                "Step-by-step guidance through the entire interview journey"
            ]
        },
        {
            number: "2",
            title: "Core Skill-Building & Knowledge Modules",
            sessions: "Self-paced + live check-ins",
            items: [
                "Airline Research & Company Fit - in-depth exploration of values, routes, safety records",
                "STAR Method Mastery - structured training with worksheets and live feedback",
                "Scenario Role-Playing - difficult passengers, emergencies, teamwork conflicts",
                "Grooming, Appearance & Presence - detailed guidance + virtual wardrobe check",
                "Psychometric Tests, Geography, and Group Exercises practice"
            ]
        },
        {
            number: "3",
            title: "Mock Interviews & Intensive Practice",
            sessions: "6-8 full-length sessions",
            items: [
                "Multiple full-length mock interviews replicating actual format",
                "Video interviews, group assessments, panel interviews practice",
                "Session recordings to review your body language and delivery",
                "Immediate, detailed feedback on content, tone, pacing, eye contact",
                "Airline-specific question banks from current recruiters"
            ]
        },
        {
            number: "4",
            title: "Refinement, Feedback Loops & Polish",
            sessions: "Ongoing support",
            items: [
                "Written debriefs after every major practice session",
                "Unlimited email and text support between calls",
                "Resume and video introduction review",
                "Confidence-building techniques - mindset shifts, breathing, visualization"
            ]
        },
        {
            number: "5",
            title: "Final Rehearsal & Post-Interview Support",
            sessions: "1-2 days before interview + after",
            items: [
                "Full \"dress rehearsal\" before your actual interview",
                "Strategy session - what to bring, follow-up tips, negotiations",
                "Post-interview debrief and results analysis",
                "Training preparation if you receive a CJO"
            ]
        }
    ];

    return (
        <div className="min-h-screen">
            {/* Hero Section */}
            <section className="py-16 md:py-24 bg-gradient-to-b from-amber-50 via-yellow-50 to-white dark:from-amber-900/20 dark:via-yellow-900/10 dark:to-background">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            <Badge className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white border-0 mb-4 px-4 py-1">
                                <Sparkles className="h-3 w-3 mr-1" />
                                Elite Mentorship
                            </Badge>
                            
                            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                                Comprehensive <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-yellow-500">Mentorship Program</span>
                            </h1>
                            
                            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                                A structured, personalized program designed specifically for aspiring flight attendants 
                                looking to excel in their interviews. Transform into a confident and polished professional 
                                who stands out in the competitive airline selection process.
                            </p>

                            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-8">
                                <div className="text-center px-6 py-4 rounded-2xl bg-gradient-to-br from-amber-100 to-yellow-100 dark:from-amber-900/30 dark:to-yellow-900/20 border border-amber-200 dark:border-amber-700">
                                    <span className="font-heading text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-yellow-500">$1,999</span>
                                    <p className="text-sm text-muted-foreground mt-1">Complete Package</p>
                                </div>
                                <div className="hidden sm:block h-12 w-px bg-border"></div>
                                <div className="text-center">
                                    <span className="font-heading text-2xl font-bold text-muted-foreground line-through">$4,500</span>
                                    <p className="text-sm text-muted-foreground mt-1">A la carte value</p>
                                </div>
                            </div>

                            <Button 
                                size="lg"
                                className="rounded-full gap-2 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white text-lg px-10 py-7 shadow-lg shadow-amber-500/25 border-0"
                                onClick={handlePurchase}
                                data-testid="purchase-mentorship-btn"
                            >
                                <ExternalLink className="h-5 w-5" />
                                <span className="flex flex-col items-start leading-tight">
                                    <span>Start Your</span>
                                    <span>Mentorship Journey</span>
                                </span>
                            </Button>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* What Makes This Different */}
            <section className="py-16">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-12"
                    >
                        <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                            What Sets This Mentorship Apart
                        </h2>
                        <p className="text-muted-foreground max-w-2xl mx-auto">
                            A great coach doesn't just provide answers to memorize; they teach you how airlines 
                            evaluate candidates based on key attributes.
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
                        {[
                            { icon: Target, title: "Customer Focus", desc: "Learn to demonstrate exceptional service mindset" },
                            { icon: FileCheck, title: "Safety Mindset", desc: "Show your commitment to passenger safety" },
                            { icon: Heart, title: "Emotional Intelligence", desc: "Handle any situation with grace and empathy" },
                            { icon: Users, title: "Teamwork", desc: "Prove you're a collaborative crew member" }
                        ].map((item, i) => (
                            <motion.div
                                key={item.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1 }}
                            >
                                <Card className="p-6 text-center h-full border-amber-200 dark:border-amber-800">
                                    <div className="h-12 w-12 rounded-full bg-gradient-to-br from-amber-100 to-yellow-100 dark:from-amber-900/30 dark:to-yellow-900/20 flex items-center justify-center mx-auto mb-4">
                                        <item.icon className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                                    </div>
                                    <h3 className="font-semibold mb-2">{item.title}</h3>
                                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                                </Card>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Program Phases */}
            <section className="py-16 bg-gradient-to-b from-amber-50/50 to-white dark:from-amber-900/10 dark:to-background">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-12"
                    >
                        <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                            Your Mentorship Journey
                        </h2>
                        <p className="text-muted-foreground max-w-2xl mx-auto">
                            A proven 5-phase approach to transform you into interview-ready material
                        </p>
                    </motion.div>

                    <div className="max-w-4xl mx-auto space-y-6">
                        {phases.map((phase, i) => (
                            <motion.div
                                key={phase.number}
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1 }}
                            >
                                <Card className="p-6 md:p-8 border-amber-200/50 dark:border-amber-800/50">
                                    <div className="flex items-start gap-4">
                                        <div className="h-10 w-10 rounded-full bg-gradient-to-r from-amber-500 to-yellow-500 text-white flex items-center justify-center font-bold flex-shrink-0">
                                            {phase.number}
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-3">
                                                <h3 className="font-heading text-xl font-semibold">{phase.title}</h3>
                                                <Badge variant="secondary" className="mt-2 md:mt-0 w-fit bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                                                    <Clock className="h-3 w-3 mr-1" />
                                                    {phase.sessions}
                                                </Badge>
                                            </div>
                                            <ul className="space-y-2">
                                                {phase.items.map((item, j) => (
                                                    <li key={j} className="flex items-start gap-2 text-sm text-muted-foreground">
                                                        <Check className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                                                        {item}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </Card>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Format & Logistics */}
            <section className="py-16">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="text-center mb-12"
                        >
                            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                                Format & Logistics
                            </h2>
                        </motion.div>

                        <div className="grid md:grid-cols-3 gap-6">
                            <Card className="p-6 text-center border-amber-200/50 dark:border-amber-800/50">
                                <Video className="h-10 w-10 text-amber-600 dark:text-amber-400 mx-auto mb-4" />
                                <h3 className="font-semibold mb-2">Virtual Sessions</h3>
                                <p className="text-sm text-muted-foreground">
                                    Most sessions held via Zoom, accessible from anywhere in the world
                                </p>
                            </Card>
                            
                            <Card className="p-6 text-center border-amber-200/50 dark:border-amber-800/50">
                                <Clock className="h-10 w-10 text-amber-600 dark:text-amber-400 mx-auto mb-4" />
                                <h3 className="font-semibold mb-2">60-Minute Sessions</h3>
                                <p className="text-sm text-muted-foreground">
                                    Each session typically lasts 60 minutes of focused coaching
                                </p>
                            </Card>
                            
                            <Card className="p-6 text-center border-amber-200/50 dark:border-amber-800/50">
                                <Calendar className="h-10 w-10 text-amber-600 dark:text-amber-400 mx-auto mb-4" />
                                <h3 className="font-semibold mb-2">4-8 Week Timeline</h3>
                                <p className="text-sm text-muted-foreground">
                                    Flexible - can be condensed to 2 weeks or extended to 2 months
                                </p>
                            </Card>
                        </div>
                    </div>
                </div>
            </section>

            {/* Mentorship Reviews Section */}
            <section className="py-16 bg-gradient-to-b from-amber-50/50 to-white dark:from-amber-900/10 dark:to-background">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="text-center mb-12"
                        >
                            <div className="flex items-center justify-center gap-1 mb-4">
                                {[1,2,3,4,5].map(i => (
                                    <Star key={i} className="h-6 w-6 fill-amber-400 text-amber-400" />
                                ))}
                            </div>
                            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-2">
                                What Our Mentorship Students Say
                            </h2>
                            <a 
                                href="/reviews" 
                                className="text-lg text-amber-600 hover:underline cursor-pointer font-semibold"
                            >
                                (212 reviews)
                            </a>
                        </motion.div>

                        <div className="grid md:grid-cols-3 gap-4 mb-8">
                            {[
                                { name: "Sarah M.", text: "The mentorship completely transformed my interview skills!", rating: 5 },
                                { name: "Jessica T.", text: "Best investment in my career - got my CJO after mentorship!", rating: 5 },
                                { name: "Amanda R.", text: "Shelly's personalized roadmap was exactly what I needed.", rating: 5 },
                                { name: "Taylor S.", text: "6 mock interviews later, I walked in with total confidence.", rating: 5 },
                                { name: "Morgan K.", text: "The STAR method training was a complete game-changer.", rating: 5 },
                                { name: "Brittany L.", text: "From nervous wreck to confident candidate - amazing!", rating: 4 },
                            ].map((review, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.1 }}
                                >
                                    <Card className="p-4 h-full border-amber-200/50 dark:border-amber-800/50">
                                        <div className="flex items-center gap-1 mb-2">
                                            {[...Array(review.rating)].map((_, j) => (
                                                <Star key={j} className="h-4 w-4 fill-amber-400 text-amber-400" />
                                            ))}
                                        </div>
                                        <p className="text-sm text-muted-foreground mb-2">"{review.text}"</p>
                                        <p className="text-xs font-medium">{review.name}</p>
                                    </Card>
                                </motion.div>
                            ))}
                        </div>

                        <div className="text-center">
                            <Button 
                                variant="outline" 
                                className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/20"
                                onClick={() => navigate('/reviews')}
                            >
                                Read All 212 Reviews
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Results Section */}
            <section className="py-16 bg-gradient-to-r from-amber-500 to-yellow-500 text-white">
                <div className="container mx-auto px-4">
                    <div className="max-w-3xl mx-auto text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                        >
                            <Award className="h-16 w-16 mx-auto mb-6 opacity-90" />
                            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                                Transform Your Mindset
                            </h2>
                            <p className="text-lg opacity-90 mb-8">
                                By the end of the mentorship, you'll walk into your interviews feeling <strong>calm, prepared, 
                                and genuinely excited</strong> - qualities that recruiters are eager to see. Many mentees report 
                                receiving job offers after just <strong>1-2 attempts</strong> because this mentorship eliminates 
                                the guesswork and fosters true confidence.
                            </p>
                            <p className="text-xl font-semibold">
                                From "I hope I pass" to "I nailed it - and earned my wings!"
                            </p>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="py-16">
                <div className="container mx-auto px-4 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                            Ready to Start Your Journey?
                        </h2>
                        <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
                            Whether you're an aspiring flight attendant or new to the airline industry seeking guidance, 
                            this framework is adaptable to your needs. I'm excited and look forward to helping you through this journey!
                        </p>
                        
                        <div className="flex flex-col items-center gap-4">
                            <Button 
                                size="lg"
                                className="rounded-full gap-2 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white text-lg px-10 py-7 shadow-lg shadow-amber-500/25 border-0"
                                onClick={handlePurchase}
                                data-testid="cta-purchase-mentorship"
                            >
                                <ExternalLink className="h-5 w-5" />
                                Get Complete Mentorship - $1,999
                            </Button>
                            <p className="text-sm text-muted-foreground">
                                <strong>Save $2,500+</strong> compared to a la carte pricing (<span className="line-through">$4,500</span> value)
                            </p>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
}
